/*
 * ॐ नमः शिवाय
 *
 * Copyright (c) 2025 Rudra Coresoft Technologies Private Limited
 *
 * This software is licensed under the VIJAYA 1.0 License.
 *
 * You are free to use, modify, and distribute this software under the terms
 * of the VIJAYA 1.0 License.
 *
 * MANDATORY ATTRIBUTION:
 * 1. The attribution to Rudra Coresoft Technologies Private Limited in this
 *    copyright notice must not be removed even after modifications.
 * 2. For Free License usage, the footer phrases "Powered by arcee.io" and
 *    "Developed by Rudra Coresoft" (plus logos) must remain visible.
 *
 * See the LICENSE file for the full text.
 */

/**
 * Which applications expose the Events screens.
 *
 * The Events wizard is an AthloHive-shaped feature living inside a
 * general-purpose admin, so it is shown for some applications and not others.
 * That used to be decided by matching the application's *name* against a
 * hard-coded list — which broke the moment an application was renamed, and made
 * a deployment's behaviour depend on a string an admin can edit in a form.
 *
 * It is deployment configuration now: REACT_APP_EVENTS_ALLOWED_APPLICATION_ID
 * names the application ids, and nothing about the feature is decided in code.
 *
 *     REACT_APP_EVENTS_ALLOWED_APPLICATION_ID=1,2,3
 *
 * Unset or empty means no application shows it — the safe default, since a
 * missing variable is far more likely to be an unconfigured environment than an
 * intent to enable the feature everywhere.
 */

/**
 * Parse the comma-separated variable into application ids.
 *
 * Whitespace around each entry is ignored, so "1, 2, 3" and "1,2,3" are the
 * same list. Entries that are not whole numbers are dropped rather than
 * silently turned into NaN — one typo in the variable must not take the whole
 * list down with it.
 *
 * @param {string|undefined|null} raw the raw environment value
 * @returns {number[]} the configured ids, in the order they were written
 */
export function parseAllowedApplicationIds(raw) {
  if (typeof raw !== 'string') return [];

  return raw
    .split(',')
    .map((entry) => entry.trim())
    .filter((entry) => /^\d+$/.test(entry))
    .map((entry) => Number(entry));
}

/**
 * True when this application is one of the configured ones.
 *
 * The id arrives from the route as a string ("4") and from the API as a number
 * (4); both must answer the same, so the comparison is made on numbers.
 *
 * @param {string|number|undefined|null} applicationId the application in view
 * @param {string|undefined|null} raw the raw environment value
 */
export function isEventsEnabledForApplication(applicationId, raw) {
  const allowed = parseAllowedApplicationIds(raw);
  if (!allowed.length) return false;

  const current = Number(applicationId);
  if (!Number.isInteger(current)) return false;

  return allowed.includes(current);
}
